/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import {
  DialogActions,
  Button,
  TextField,
  DialogContent,
  DialogTitle,
  Dialog,
  Typography,
  IconButton,
  Checkbox,
  FormControlLabel,
  makeStyles,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import SaveIcon from "@material-ui/icons/Save";

import ShowNotification from "../../../components/react-notifications/react-notifications";
import { NotificationMessageType } from "../../../utils/configuration";
import * as viVN from "../../../language/vi-VN.json";
import { toSlug, getInvestmentErrorMessage } from "../../../common/tools";
import * as masterAction from "../../../redux/store/investment-opportunity/investment-master.store";

const useStyles = makeStyles((theme) => ({
  closeButton: {
    position: "absolute",
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}));

const DefaultModel = {
  code: "",
  name: "",
  slug: "",
  description: "",
  sortOrder: "0",
  isActive: true,
};

const validate = (model, hasSlug) => {
  const errors = {};
  const code = model.code.trim();
  const name = model.name.trim();
  const slug = model.slug.trim();

  if (!code) errors.code = "Trường này là bắt buộc";
  else if (code.length > 50) errors.code = "Tối đa 50 ký tự";

  if (!name) errors.name = "Trường này là bắt buộc";
  else if (name.length > 255) errors.name = "Tối đa 255 ký tự";

  if (hasSlug) {
    if (!slug) errors.slug = "Trường này là bắt buộc";
    else if (slug.length > 255) errors.slug = "Tối đa 255 ký tự";
  }

  if (!/^\d+$/.test(String(model.sortOrder).trim()))
    errors.sortOrder = "Thứ tự là số nguyên ≥ 0";

  return errors;
};

const InvestmentMasterForm = ({
  isOpen,
  onClose,
  onSuccess,
  kind,
  title,
  hasSlug,
  id,
}) => {
  const classes = useStyles();
  const isEdit = !!id;
  const [model, setModel] = useState(DefaultModel);
  const [isLoaded, setIsLoaded] = useState(!isEdit);
  const [isSlugTouched, setIsSlugTouched] = useState(isEdit);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit) return;
    masterAction
      .GetDetailMaster(kind, id)
      .then((res) => {
        const content = res && res.content;
        if (content) {
          setModel({
            code: content.code || "",
            name: content.name || "",
            slug: content.slug || "",
            description: content.description || "",
            sortOrder: String(content.sortOrder ?? 0),
            isActive: !!content.isActive,
          });
          setIsLoaded(true);
        }
      })
      .catch((err) => {
        ShowNotification(
          getInvestmentErrorMessage(err),
          NotificationMessageType.Error
        );
        onClose();
      });
  }, [id]);

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setModel((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "name" && hasSlug && !isSlugTouched) {
        next.slug = toSlug(value);
      }
      return next;
    });
    if (field === "slug") setIsSlugTouched(true);
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const onSubmit = (event) => {
    event.preventDefault();
    const validateErrors = validate(model, hasSlug);
    setErrors(validateErrors);
    if (Object.keys(validateErrors).length > 0) return;

    const body = {
      code: model.code.trim().toUpperCase(),
      name: model.name.trim(),
      description: model.description.trim() || null,
      sortOrder: parseInt(model.sortOrder, 10),
      isActive: model.isActive,
    };
    if (hasSlug) body.slug = toSlug(model.slug);

    setIsSubmitting(true);
    const request = isEdit
      ? masterAction.UpdateMaster(kind, id, body)
      : masterAction.CreateMaster(kind, body);
    request
      .then(() => {
        setIsSubmitting(false);
        ShowNotification(
          isEdit
            ? viVN.Success.InvestmentMasterEditSuccess
            : viVN.Success.InvestmentMasterAddSuccess,
          NotificationMessageType.Success
        );
        onSuccess();
      })
      .catch((err) => {
        setIsSubmitting(false);
        ShowNotification(
          getInvestmentErrorMessage(err),
          NotificationMessageType.Error
        );
      });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} fullWidth={true} maxWidth="sm">
      <DialogTitle disableTypography className="border-bottom">
        <Typography variant="h6">
          {isEdit ? "Chỉnh sửa " : "Thêm "}
          {title}
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <form onSubmit={onSubmit} autoComplete="off">
        {isLoaded && (
          <DialogContent className="pt-4 pb-2">
            <div className="row">
              <div className="form-group col-md-4">
                <label className="text-dark">
                  Mã<span className="required"></span>
                </label>
                <TextField
                  fullWidth
                  value={model.code}
                  onChange={handleChange("code")}
                  onBlur={() =>
                    setModel((prev) => ({
                      ...prev,
                      code: prev.code.trim().toUpperCase(),
                    }))
                  }
                  inputProps={{ maxLength: 50 }}
                  error={!!errors.code}
                />
                {errors.code && <span className="error">{errors.code}</span>}
              </div>
              <div className="form-group col-md-8">
                <label className="text-dark">
                  Tên<span className="required"></span>
                </label>
                <TextField
                  fullWidth
                  value={model.name}
                  onChange={handleChange("name")}
                  inputProps={{ maxLength: 255 }}
                  error={!!errors.name}
                />
                {errors.name && <span className="error">{errors.name}</span>}
              </div>
            </div>

            {hasSlug && (
              <div className="form-group">
                <label className="text-dark">
                  Slug<span className="required"></span>
                </label>
                <TextField
                  fullWidth
                  value={model.slug}
                  onChange={handleChange("slug")}
                  onBlur={() =>
                    setModel((prev) => ({ ...prev, slug: toSlug(prev.slug) }))
                  }
                  inputProps={{ maxLength: 255 }}
                  error={!!errors.slug}
                  helperText="Tự sinh từ tên, có thể sửa. Dùng làm đường dẫn ở web người dùng."
                />
                {errors.slug && <span className="error">{errors.slug}</span>}
              </div>
            )}

            <div className="form-group">
              <label className="text-dark">Mô tả</label>
              <textarea
                rows="3"
                className="form-control"
                value={model.description}
                onChange={handleChange("description")}
              />
            </div>

            <div className="row">
              <div className="form-group col-md-4">
                <label className="text-dark">
                  Thứ tự<span className="required"></span>
                </label>
                <TextField
                  fullWidth
                  type="number"
                  value={model.sortOrder}
                  onChange={handleChange("sortOrder")}
                  inputProps={{ min: 0, step: 1 }}
                  error={!!errors.sortOrder}
                />
                {errors.sortOrder && (
                  <span className="error">{errors.sortOrder}</span>
                )}
              </div>
              <div className="form-group col-md-8 d-flex align-items-end">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={model.isActive}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setModel((prev) => ({ ...prev, isActive: checked }));
                      }}
                      color="primary"
                    />
                  }
                  label="Hoạt động (tắt thì không chọn được khi tạo/sửa dự án)"
                />
              </div>
            </div>
          </DialogContent>
        )}

        <DialogActions className="border-top">
          <Button
            type="button"
            onClick={onClose}
            variant="contained"
            startIcon={<CloseIcon />}
          >
            Hủy
          </Button>
          <Button
            type="submit"
            color="primary"
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!isLoaded || isSubmitting}
          >
            Lưu
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default InvestmentMasterForm;
